import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { User, Award, Flame, Edit2, Lightbulb, Zap, Sparkles, AlertTriangle } from "lucide-react";

export function OverviewTab({
  selectedChild,
  usageStats,
  subscription,
  subStatus,
  assessments,
  formatDate,
  setShowEditModal,
  setActiveTab,
  homeworkCredits,
  setShowTopUpModal,
  setShowReportModal
}) {
  // Calculate credit percentage for progress bar
  const creditPercentage = homeworkCredits 
    ? Math.min(100, (homeworkCredits.remaining_credits / Math.max(homeworkCredits.total_credits, 15)) * 100)
    : 0;
  
  return (
    <div className="space-y-4">
      {/* Homework Credits Banner - Compact Display */}
      <Card className="border border-purple-200 bg-gradient-to-r from-purple-50 to-indigo-50 overflow-hidden">
        <CardContent className="py-3">
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-md">
                <Lightbulb className="h-5 w-5 text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 text-sm flex items-center gap-2">
                  EduAssist Homework Credits
                  {homeworkCredits?.is_free_trial && homeworkCredits?.free_trial_remaining > 0 && (
                    <span className="text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full font-medium">
                      FREE TRIAL
                    </span>
                  )}
                </h3>
                <p className="text-xs text-gray-600">AI homework help via WhatsApp</p>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 w-full lg:w-auto">
              {/* Credits Display */}
              <div className="flex-1 lg:flex-none lg:min-w-[160px]">
                <div className="flex items-baseline gap-1.5 mb-1">
                  <span className="text-2xl font-bold text-purple-700">
                    {homeworkCredits?.remaining_credits ?? 15}
                  </span>
                  <span className="text-xs text-gray-500">credits remaining</span>
                </div>
                <Progress value={creditPercentage} className="h-1.5 bg-purple-100" />
              </div>
              
              {/* Top-Up Button */}
              <Button
                onClick={() => setShowTopUpModal(true)}
                size="sm"
                className="bg-purple-600 hover:bg-purple-700 shadow-sm h-8 text-xs"
                data-testid="topup-credits-btn"
              >
                <Zap className="h-3.5 w-3.5 mr-1.5" />
                Top Up
              </Button>
            </div>
          </div>
          
          {/* Low credits warning - Compact */}
          {homeworkCredits && homeworkCredits.remaining_credits <= 5 && homeworkCredits.remaining_credits > 0 && (
            <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded-md">
              <span className="text-amber-600 text-xs">
                ⚠️ Running low on credits! Top up now to keep learning uninterrupted.
              </span>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Child Profile Card - Compact */}
        <Card className="lg:col-span-2">
          <CardHeader className="py-3 px-4 flex flex-row items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2 text-base">
                <User className="h-4 w-4 text-blue-600" />
                {selectedChild?.name || "Child"}&apos;s Profile
                <span
                  data-testid="child-board-badge"
                  className={`ml-1 inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${
                    (selectedChild?.board || "cbse") === "ib"
                      ? "bg-purple-100 text-purple-700 border border-purple-200"
                      : "bg-emerald-100 text-emerald-700 border border-emerald-200"
                  }`}
                >
                  {(selectedChild?.board || "cbse") === "ib" ? "IB" : "CBSE"}
                </span>
              </CardTitle>
              <CardDescription className="text-xs">Student ID: {selectedChild?.student_id}</CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={() => setShowEditModal(true)} className="h-7 text-xs" data-testid="edit-child-btn">
              <Edit2 className="h-3 w-3 mr-1" />
              Edit
            </Button>
          </CardHeader>
          <CardContent className="pt-0 pb-3 px-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <div className="p-2 bg-gray-50 rounded-md">
                <p className="text-[10px] text-gray-500 uppercase">Age</p>
                <p className="font-semibold text-sm">{selectedChild?.age} years</p>
              </div>
              <div className="p-2 bg-gray-50 rounded-md">
                <p className="text-[10px] text-gray-500 uppercase">Grade</p>
                <p className="font-semibold text-sm">Grade {selectedChild?.grade}</p>
              </div>
              <div className="p-2 bg-gray-50 rounded-md">
                <p className="text-[10px] text-gray-500 uppercase">School</p>
                <p className="font-semibold text-sm truncate">{selectedChild?.school_name || "N/A"}</p>
              </div>
              <div className="p-2 bg-gray-50 rounded-md">
                <p className="text-[10px] text-gray-500 uppercase">Subjects</p>
                <p className="font-semibold text-sm truncate">{selectedChild?.subjects_required || "Not set"}</p>
              </div>
            </div>
            <button
              type="button"
              data-testid="report-mismatch-btn"
              onClick={() => setShowReportModal && setShowReportModal(true)}
              className="mt-3 inline-flex items-center gap-1 text-[11px] text-gray-400 hover:text-amber-600 transition-colors"
            >
              <AlertTriangle className="h-3 w-3" />
              Content doesn&apos;t match the {(selectedChild?.board || "cbse") === "ib" ? "IB" : "CBSE"} syllabus? Report a mismatch
            </button>
          </CardContent>
        </Card>

        {/* Quick Stats Card - Compact */}
        <Card>
          <CardHeader className="py-3 px-4">
            <CardTitle className="flex items-center gap-2 text-base">
              <Award className="h-4 w-4 text-emerald-600" />
              Quick Stats
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0 pb-3 px-4 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-gray-600 text-sm">Questions</span>
              <span className="font-bold">{selectedChild?.total_questions || 0}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600 text-sm">Accuracy</span>
              <span className="font-bold text-emerald-600">
                {selectedChild?.total_questions > 0 
                  ? Math.round((selectedChild.correct_answers / selectedChild.total_questions) * 100)
                  : 0}%
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600 text-sm">Current Streak</span>
              <span className="font-bold text-orange-600 flex items-center gap-1">
                <Flame className="h-3.5 w-3.5" />
                {usageStats?.current_streak || 0} days
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600 text-sm">Level</span>
              <span className="font-bold text-purple-600">{selectedChild?.assessment_level || 1}</span>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
